import { apiv2Get, apiv2GetEnvelope } from '@/lib/apiv2';
import { eventPost } from '@/lib/events';
import { flattenMilestones, NON_TERMINAL_STATUSES, type CarePeriodLike } from '@/logic/carePeriod';
import { cleanActorOf, type CompletionActor } from '@/logic/task';

export interface CarePeriod extends CarePeriodLike {
  care_period_id: string | number;
  withings_user_id: string | number;
  status?: string;
  programid?: string;
}

export async function getCarePeriodPage(programid: string, offset: number, limit = 100): Promise<{
  status: string; data: CarePeriod[]; meta?: { count?: number | string };
}> {
  return apiv2GetEnvelope('care-periods', {
    programid, offset, limit, sort: 'created',
  });
}

export async function getCarePeriodByUser(withingsUserId: string | number): Promise<CarePeriod | null> {
  const rows = await apiv2Get('care-periods', {
    withings_user_id: String(withingsUserId),
    status: NON_TERMINAL_STATUSES,
    limit: 1,
  });
  return rows && rows.length > 0 ? flattenMilestones(rows[0]) : null;
}

export interface WithdrawPatientInput {
  withingsUserId: string | number;
  carePeriodId?: string | number;
  /** Already encoded by `encodeWithdrawalReason` — `op:<code>` or `op:other:<note>`. */
  withdrawalReason?: string | null;
}

// WF67 flips the period to `withdrawn` + revokes the Withings device subscriptions.
// Async event — apiv2 acks immediately, there is no verdict to read back.
export async function withdrawPatient({
  withingsUserId,
  carePeriodId,
  withdrawalReason,
}: WithdrawPatientInput): Promise<unknown> {
  return eventPost('patient_withdrawal_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    // eventPost drops null/undefined, so an absent reason sends no key at all —
    // which is what keeps WF67 inert until the app half ships.
    withdrawal_reason: withdrawalReason ?? null,
    occurred_at: new Date().toISOString(),
    trigger_source: 'backoffice',
  });
}

export interface RequestWithdrawalInput {
  withingsUserId: string | number;
  carePeriodId: string;
  /** From `encodeRequestReason` — `op:<code>`, never a note. */
  withdrawalReason: string;
  /** Names the staff user on WF67's care-team Chat line. */
  actor?: CompletionActor;
}

// A separate call, so a request can never turn into a withdrawal by a forgotten flag: WF67 writes
// a coordinator task and touches nothing else. Async event, like the withdrawal.
export async function requestWithdrawal({
  withingsUserId,
  carePeriodId,
  withdrawalReason,
  actor,
}: RequestWithdrawalInput): Promise<unknown> {
  const cleanActor = cleanActorOf(actor);
  return eventPost('patient_withdrawal_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    withdrawal_mode: 'request',
    withdrawal_reason: withdrawalReason,
    occurred_at: new Date().toISOString(),
    trigger_source: 'backoffice',
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

export interface CancelWithdrawalRequestInput {
  withingsUserId: string | number;
  /** The request's own period, which may not be the one the patient file shows. */
  carePeriodId: string;
  actor?: CompletionActor;
}

// WF67's coordinator-only cancel (apiv2 scopes this event to coordinator sessions); WF17 refuses
// every task_updated on a request. Async, like the request.
export async function cancelWithdrawalRequest({
  withingsUserId,
  carePeriodId,
  actor,
}: CancelWithdrawalRequestInput): Promise<unknown> {
  const cleanActor = cleanActorOf(actor);
  return eventPost('patient_withdrawal_requested', {
    id_dev_user: String(withingsUserId),
    care_period_id: carePeriodId,
    withdrawal_mode: 'cancel_request',
    occurred_at: new Date().toISOString(),
    trigger_source: 'backoffice',
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

export async function getActiveCareperiodId(
  withingsUserId: string | number,
): Promise<string | number | null> {
  const rows = await apiv2Get('care-periods', {
    withings_user_id: String(withingsUserId),
    status: 'active',
    limit: 1,
  });
  return rows && rows.length > 0 ? rows[0].care_period_id : null;
}
